/**
 * bankMatching/normalize — Text-, IBAN- und Verwendungszweck-Normalisierung
 * für die Matching-Engine (§8.1). Pure, ohne Seiteneffekte.
 *
 * Determinismus: gleicher Input ⇒ gleicher Output. Keine Locale-abhängigen
 * Operationen (Umlaut-Transliteration ist explizit, nicht via `localeCompare`).
 *
 * @module bankMatching/normalize
 */

// ─────────────────────────────────────────────────────────────────────────
// Verwendungszweck / Rechnungsnummer
// ─────────────────────────────────────────────────────────────────────────

/**
 * Normalisiert eine Referenz (Verwendungszweck oder Rechnungsnummer):
 * Uppercase, alles außer `[A-Z0-9]` entfernt.
 *
 * `'Re2026-042'` → `'RE2026042'`, `'  re 2026 042 '` → `'RE2026042'`.
 *
 * @param s - Roh-String (darf leer sein).
 * @returns Normalisierte Referenz (nur `[A-Z0-9]`).
 */
export function normalizeRef(s: string): string {
  if (!s) return '';
  return s.toUpperCase().replace(/[^A-Z0-9]/g, '');
}

/**
 * Wie {@link normalizeRef}, aber mit vorgeschalteter Umlaut-Transliteration
 * (ä→AE, ö→OE, ü→UE, ß→SS), damit deutsche Nativ-Schreibweisen im
 * Verwendungszweck erkennbar werden (§8.9). `normalizeRef` ENTFERNT Umlaute
 * ersatzlos (`'Rücklastschrift'` → `'RCKLASTSCHRIFT'`) und würde spec-geforderte
 * Return-Token wie `RUECKLASTSCHRIFT` nie treffen.
 *
 * Bewusst getrennt von `normalizeRef` gehalten: Die Rechnungsnummer-Erkennung
 * (§8.3, `refContains`) arbeitet mit ASCII-Nummern und darf sich nicht ändern.
 * Diese Variante wird ausschließlich für den Return-Indikator-Vergleich (§8.9)
 * genutzt.
 *
 * @param s - Roh-String (darf leer sein).
 * @returns Transliterierte, normalisierte Referenz (nur `[A-Z0-9]`).
 */
export function normalizeReturnRef(s: string): string {
  if (!s) return '';
  return transliterate(s.toLowerCase())
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');
}

/**
 * Prüft, ob eine normalisierte Rechnungsnummer als Referenz im normalisierten
 * Verwendungszweck vorkommt (§8.1). Grenzen-Check gegen False-Positives:
 *
 * - `refNorm.length >= 5` — kurze Nummern (`'1'`, `'42'`) sind
 *   False-Positive-Magnete und zählen NIE als Nummern-Signal.
 * - Ein Treffer zählt NUR, wenn das Zeichen unmittelbar NACH dem Fund KEINE
 *   Ziffer ist — sonst matcht `RE2026042` fälschlich in `RE20260421`.
 *   Alle Vorkommen werden geprüft; genügt EINES der Grenzen-Bedingung, ist es
 *   ein Treffer.
 *
 * @param purposeNorm - bereits `normalizeRef`-normalisierter Verwendungszweck.
 * @param refNorm - bereits `normalizeRef`-normalisierte Rechnungsnummer.
 * @returns `true` bei gültigem Treffer.
 */
export function refContains(purposeNorm: string, refNorm: string): boolean {
  if (refNorm.length < 5) return false;
  if (!purposeNorm) return false;

  let idx = purposeNorm.indexOf(refNorm);
  while (idx !== -1) {
    const after = purposeNorm.charAt(idx + refNorm.length);
    // charAt liefert '' am String-Ende → kein Ziffer-Zeichen → gültiger Treffer.
    if (after === '' || after < '0' || after > '9') {
      return true;
    }
    idx = purposeNorm.indexOf(refNorm, idx + 1);
  }
  return false;
}

/**
 * Liefert die Teilmenge bekannter (offener) Rechnungsnummern, deren
 * normalisierte Form im Verwendungszweck vorkommt (§8.5, Split-Erkennung).
 * Reihenfolge und Eindeutigkeit stabil (Eingabe-Reihenfolge, dedupliziert).
 *
 * @param purpose - Roh-Verwendungszweck.
 * @param knownNumbers - Liste offener Rechnungsnummern (roh).
 * @returns Getroffene Rechnungsnummern (Original-Schreibweise, dedupliziert).
 */
export function extractKnownInvoiceNumbers(
  purpose: string,
  knownNumbers: string[],
): string[] {
  const purposeNorm = normalizeRef(purpose);
  if (!purposeNorm) return [];

  const hits: string[] = [];
  const seen = new Set<string>();
  for (const num of knownNumbers) {
    if (seen.has(num)) continue;
    if (refContains(purposeNorm, normalizeRef(num))) {
      hits.push(num);
      seen.add(num);
    }
  }
  return hits;
}

// ─────────────────────────────────────────────────────────────────────────
// Namens-Ähnlichkeit
// ─────────────────────────────────────────────────────────────────────────

/**
 * Rechtsform-/Füllwort-Stopwords (Kleinschreibung, nach Transliteration).
 * Werden vor dem Token-Vergleich entfernt, damit „Telekom Deutschland GmbH"
 * und „TELEKOM DEUTSCHLAND" hoch ähnlich sind.
 */
const NAME_STOPWORDS = new Set<string>([
  'gmbh',
  'mbh',
  'ag',
  'ug',
  'kg',
  'kgaa',
  'ohg',
  'gbr',
  'ek',
  'e',
  'k',
  'co',
  'se',
  'haftungsbeschraenkt',
  'inc',
  'ltd',
  'llc',
  'plc',
  'und',
]);

/**
 * Transliteriert deutsche Umlaute/ß auf ASCII-Digraphe (deterministisch,
 * ohne Locale). Wird vor der Tokenisierung angewendet.
 */
function transliterate(s: string): string {
  return s
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');
}

/**
 * Normalisiert einen Firmen-/Personennamen zu einer Token-Menge (§8.1):
 * lowercase → Umlaut-Transliteration → Split an Nicht-Alphanumerik →
 * Stopword-Filter. Leere/`null`-Eingaben ergeben eine leere Liste.
 *
 * @param s - Roh-Name (oder `null`/leer).
 * @returns Bereinigte Tokens (kann leer sein).
 */
export function normalizeName(s: string | null | undefined): string[] {
  if (!s) return [];
  const lowered = transliterate(s.toLowerCase());
  const rawTokens = lowered.split(/[^a-z0-9]+/);
  const tokens: string[] = [];
  for (const t of rawTokens) {
    if (t === '') continue;
    if (NAME_STOPWORDS.has(t)) continue;
    tokens.push(t);
  }
  return tokens;
}

/**
 * Token-Overlap-Ähnlichkeit zweier Namen (§8.1):
 * `|Schnitt| / min(|A|,|B|)` ∈ [0,1]. Leere Token-Menge (nach Stopword-Filter)
 * auf einer Seite → `0` (NIE `NaN` — Edge-Case-Pflichttest §8.8).
 *
 * @param a - Roh-Name A (oder `null`).
 * @param b - Roh-Name B (oder `null`).
 * @returns Ähnlichkeit ∈ [0,1].
 */
export function nameSimilarity(
  a: string | null | undefined,
  b: string | null | undefined,
): number {
  const tokensA = normalizeName(a);
  const tokensB = normalizeName(b);
  if (tokensA.length === 0 || tokensB.length === 0) return 0;

  const setB = new Set(tokensB);
  const seen = new Set<string>();
  let intersection = 0;
  for (const t of tokensA) {
    if (seen.has(t)) continue; // Token-MENGEN-Vergleich (Duplikate zählen einmal)
    seen.add(t);
    if (setB.has(t)) intersection += 1;
  }

  const denom = Math.min(new Set(tokensA).size, setB.size);
  if (denom === 0) return 0;
  return intersection / denom;
}

// ─────────────────────────────────────────────────────────────────────────
// IBAN
// ─────────────────────────────────────────────────────────────────────────

/**
 * Normalisiert eine IBAN: Uppercase, alle Whitespaces entfernt (§8.1).
 *
 * @param s - Roh-IBAN (oder `null`).
 * @returns Normalisierte IBAN oder `null` bei leerer/`null`-Eingabe.
 */
export function normalizeIban(s: string | null | undefined): string | null {
  if (!s) return null;
  const normalized = s.toUpperCase().replace(/\s+/g, '');
  return normalized === '' ? null : normalized;
}

// ─────────────────────────────────────────────────────────────────────────
// Datums-Helfer (ISO YYYY-MM-DD, UTC — kein Locale/DST-Drift)
// ─────────────────────────────────────────────────────────────────────────

/**
 * Signierte Kalendertage-Differenz `a − b` (UTC, DST-frei).
 * `isoDaysDiff('2026-01-10','2026-01-01') === 9`.
 *
 * @param a - ISO `YYYY-MM-DD`.
 * @param b - ISO `YYYY-MM-DD`.
 * @returns Ganzzahlige Tagesdifferenz (a − b), kann negativ sein.
 */
export function isoDaysDiff(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  const ta = Date.UTC(ay, am - 1, ad);
  const tb = Date.UTC(by, bm - 1, bd);
  return Math.round((ta - tb) / 86_400_000);
}

/**
 * Addiert `days` Kalendertage zu einer ISO-Datumszeichenkette (UTC, DST-frei).
 * Negative `days` sind erlaubt. Identische Semantik wie `openItemStatus.addDaysToISO`,
 * hier lokal gehalten, um die reine Engine nicht an das M1-Modul zu koppeln.
 *
 * @param isoDate - `YYYY-MM-DD`.
 * @param days - Ganzzahl (auch negativ).
 * @returns `YYYY-MM-DD` Ergebnisdatum.
 */
export function addDaysToISO(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  date.setUTCDate(date.getUTCDate() + days);
  const yy = date.getUTCFullYear();
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(date.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}
